import { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';

import { getClassNames } from '../../../../../utils/util';

import { ReactComponent as ArrowSingle } from '../../../../../resources/icons/arrow-single.svg';

import './currency-dropdown.scss';

const CurrencyDropdown = ({ value, options, onChange }) => {
  const [showOptions, setShowOptions] = useState(false);

  const dropdownRef = useRef(null);

  const handleClickOutside = useCallback((event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setShowOptions(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);

  const handleOptionMouseDown = (option) => {
    onChange(option.code);
    setShowOptions(false);
  };

  const renderValue = () => {
    if (!value) {
      return <span className="currency-dropdown__placeholder">Select...</span>;
    }

    const selectedOption = options.find((option) => option.code === value);

    return (
      <span className="currency-dropdown__value">
        <span className="currency-dropdown__code">{selectedOption.code}</span>
        <span className="currency-dropdown__name">{selectedOption.name}</span>
      </span>
    );
  };

  const renderOption = (option) => {
    const { code, name } = option;

    let selected = false;

    if (value && value === code) {
      selected = true;
    }

    return (
      <li
        className={getClassNames('currency-dropdown__option', { selected })}
        key={code}
        onMouseDown={() => handleOptionMouseDown(option)}
      >
        <span className="currency-dropdown__code">{code}</span>
        <span className="currency-dropdown__name">{name}</span>
      </li>
    );
  };

  const renderOptions = () => {
    if (options.length === 0) {
      return <li className="currency-dropdown__option currency-dropdown__option--empty">No Options</li>;
    }

    const copyOfOptions = [...options];

    const sortedOptions = copyOfOptions.sort((optionOne, optionTwo) => {
      if (optionOne.code > optionTwo.code) return 1;
      if (optionOne.code < optionTwo.code) return -1;
      return 0;
    });

    const priorityOptions = sortedOptions.filter((option) => ['USD', 'EUR', 'GBP', 'ZAR'].includes(option.code));
    const otherOptions = sortedOptions.filter((option) => !['USD', 'EUR', 'GBP', 'ZAR'].includes(option.code));

    const elements = [];
    priorityOptions.forEach((option) => elements.push(renderOption(option)));

    if (priorityOptions.length > 0 && otherOptions.length > 0) {
      elements.push(<li className="currency-dropdown__option-separator" key="divider" />);
    }

    otherOptions.forEach((option) => elements.push(renderOption(option)));

    return elements;
  };

  return (
    <div className="currency-dropdown" ref={dropdownRef}>
      <button className="currency-dropdown__value-container" onClick={() => setShowOptions(!showOptions)}>
        {renderValue()}
        <ArrowSingle className={getClassNames('currency-dropdown__icon', { open: showOptions })} />
      </button>
      {showOptions && <ul className="currency-dropdown__options">{renderOptions()}</ul>}
    </div>
  );
};

CurrencyDropdown.propTypes = {
  value: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onChange: PropTypes.func.isRequired,
};

CurrencyDropdown.defaultProps = {
  value: undefined,
};

export default CurrencyDropdown;
